import {
  handleErrorMessage,
  isPhoneValid,
  isTruthy,
  openSuccessNotification,
} from "helpers/methods";
import {
  fetchRoles,
  resetMFA,
  unlockUser,
  updateUser,
} from "../SettingsService";
import { addUser, fetchTeamCount, fetchTeamData } from "./TeamServices";
import { pageFilters } from "models/interfaces";
import { AddTeamFields, TeamData } from "types/team";
import strings from "global/constants/StringConstants";
import { getSeatBlockReason, SeatBlockReason } from "helpers/perSeatHelpers";
import { focusFirstInvalidField } from "utils/accessibility";

export const teamFieldIds = {
  firstName: "first-name",
  lastName: "last-name",
  contactNo: "contactNo",
  email: "email",
  roleIds: "role-id",
};

export const getTeamData = async (
  setLoading: Function,
  convertDataToTableFormat: any,
  setRoles: Function,
  setCount: Function,
  setTeam: Function,
  teamFilters: pageFilters,
) => {
  const { searchText, page, rowsPerPage } = teamFilters;
  try {
    setLoading(true);
    const [roles, count, response] = await Promise.all([
      fetchRoles(),
      fetchTeamCount(searchText),
      fetchTeamData(rowsPerPage, page, searchText),
    ]);
    const tableData = convertDataToTableFormat(response, roles);
    setCount(count);
    setRoles(
      roles.map((role: any) => {
        return {
          id: role.id,
          name: role.name,
        };
      }),
    );
    setTeam([...tableData]);
  } catch (error: any) {
    handleErrorMessage(error);
  } finally {
    setLoading(false);
  }
};

export const handleUnlockUser = async (
  email: string,
  setLoading: Function,
  convertDataToTableFormat: any,
  setRoles: Function,
  setCount: Function,
  setTeam: Function,
  teamFilters: pageFilters,
) => {
  try {
    // No skeleton for the unlock call itself — only `getTeamData` below,
    // once there's actually new data to show, same as reset-2FA and every
    // other row action.
    await unlockUser(email);
    openSuccessNotification(
      `Account associated with ${email} has been unlocked successfully.`,
    );
    await getTeamData(
      setLoading,
      convertDataToTableFormat,
      setRoles,
      setCount,
      setTeam,
      teamFilters,
    );
  } catch (error: any) {
    setLoading(false);
    handleErrorMessage(error);
  }
};

export const handleResetMFA = async (
  email: string,
  setLoading: Function,
  convertDataToTableFormat: any,
  setRoles: Function,
  setCount: Function,
  setTeam: Function,
  teamFilters: pageFilters,
) => {
  try {
    await resetMFA(email);
    openSuccessNotification(`MFA has been reset for ${email}.`);
    await getTeamData(
      setLoading,
      convertDataToTableFormat,
      setRoles,
      setCount,
      setTeam,
      teamFilters,
    );
  } catch (error: any) {
    setLoading(false);
    handleErrorMessage(error);
  }
};

export const getRoleName = (
  id: number,
  allRoles: { id: number; name: string }[],
) => {
  const findRole = allRoles.find((role) => role.id === id);
  if (findRole) {
    return findRole.name;
  }
  return "";
};

export const handleMenuItemClick = async (
  action: string,
  setAnchorEl: Function,
  setLoading: Function,
  convertDataToTableFormat: any,
  setRoles: Function,
  setCount: Function,
  setTeam: Function,
  teamFilters: pageFilters,
  selectedUser: any,
  selectedUserData: any,
  setInviteUser: Function,
  setIsEdit: Function,
) => {
  switch (action) {
    case "edit":
      setAnchorEl(null);
      setIsEdit(true);
      setInviteUser({
        visible: true,
        metadata: {
          email: selectedUserData.email,
          name: selectedUserData.name,
          firstName: selectedUserData.firstName,
          lastName: selectedUserData.lastName,
          roleIds: selectedUserData.roleIds,
          id: selectedUserData.id,
          contactNo: selectedUserData.contactNo,
        },
      });
      break;
    case "unlock":
      setAnchorEl(null);
      await handleUnlockUser(
        selectedUser.email,
        setLoading,
        convertDataToTableFormat,
        setRoles,
        setCount,
        setTeam,
        teamFilters,
      );
      break;
    case "reset2FA":
      setAnchorEl(null);
      await handleResetMFA(
        selectedUser.email,
        setLoading,
        convertDataToTableFormat,
        setRoles,
        setCount,
        setTeam,
        teamFilters,
      );
      break;
      // case "activate":
      //   setAnchorEl(null);
      break;
    default:
      break;
  }
};

export const handleAddUpdateOnSubmit = async (
  user: AddTeamFields,
  edit: boolean,
  errors: { [key: string]: any },
  setErrors: Function,
  setLoading: Function,
  handleDialogClose: Function,
  setInviteUser: Function,
  convertDataToTableFormat: any,
  setRoles: Function,
  setCount: Function,
  setTeam: Function,
  teamFilters: pageFilters,
  /**
   * Called instead of the generic error toast when the API refuses the seat on
   * purpose — `406`, i.e. every purchased seat is taken. Inviting someone never
   * charges, so a `402` is not expected here.
   */
  onSeatBlocked?: (reason: SeatBlockReason, message?: string) => void,
  /**
   * Refetches the seat card's sources. Inviting someone takes a seat, so the
   * "N of M purchased seats in use" meter is stale the moment this succeeds —
   * it used to sit on the old number until the page was reloaded by hand.
   */
  onSeatsChanged?: () => Promise<void> | void,
  /**
   * The Team page's own loading flag, which its table's skeleton is driven
   * by. `setLoading` above is the dialog's local spinner state — it stops
   * mattering the moment the dialog closes, so the refetch has to drive this
   * one instead or the table sits there with no loading indicator at all
   * while it refreshes.
   */
  setTableLoading?: Function,
) => {
  const [isValid, validationErrors] = validation(user, errors, setErrors);
  if (!isValid) {
    focusFirstInvalidField([
      validationErrors.firstName && teamFieldIds.firstName,
      validationErrors.lastName && teamFieldIds.lastName,
      validationErrors.contactNo && teamFieldIds.contactNo,
      validationErrors.email && teamFieldIds.email,
      validationErrors.roleIds && teamFieldIds.roleIds,
    ]);
  }
  if (isValid) {
    try {
      setLoading(true);
      const body: AddTeamFields = {
        account: "sign",
        roleIds: user.roleIds.map(
          (role: { id: number; name: string }) => role.id,
        ),
        id: user.id,
        email: user.email.trim(),
        name: `${user.firstName?.trim() || ""} ${user.lastName?.trim() || ""}`,
        contactNo: user.contactNo.trim(),
      };
      edit ? await updateUser(body) : await addUser(body);
      openSuccessNotification(
        edit ? "User updated successfully!" : "User added successfully!",
      );
      setInviteUser({
        visible: false,
        metadata: {
          email: "",
          name: "",
          firstName: "",
          lastName: "",
          id: 0,
          roleIds: [],
        },
      });
      handleDialogClose();
      setLoading(false);
      // Both refetches start together and toggle their own skeleton
      // independently (the table's via `getTeamData`, the seats card's inside
      // `onSeatsChanged`) — one running long must not hold the other's data
      // back from showing the moment it lands.
      await Promise.all([
        getTeamData(
          setTableLoading ?? setLoading,
          convertDataToTableFormat,
          setRoles,
          setCount,
          setTeam,
          teamFilters,
        ),
        onSeatsChanged?.(),
      ]);
    } catch (error: any) {
      setLoading(false);
      handleDialogClose();
      const reason = getSeatBlockReason(error);
      if (reason && onSeatBlocked) {
        onSeatBlocked(reason, error?.message);
        return;
      }
      handleErrorMessage(error);
    }
  }
};

export const validation = (
  user: AddTeamFields,
  errors: { [key: string]: any },
  setErrors: Function,
) => {
  let isValid = true;
  const emailRegex = strings.regex;
  let error = { ...errors };
  const firstName = user.firstName;
  const lastName = user.lastName;
  const email = user.email;
  const contactNo = user.contactNo;
  const roleIds = user.roleIds;
  error = {
    email: "",
    firstName: "",
    lastName: "",
    contactNo: "",
    roleIds: "",
  };

  if (!isTruthy(firstName)) {
    error.firstName = "Please enter first name";
    isValid = false;
  }

  if (!isTruthy(lastName)) {
    error.lastName = "Please enter last name";
    isValid = false;
  }

  if (!isTruthy(email)) {
    error.email = "Please enter email!";
    isValid = false;
  } else if (!emailRegex.test(email)) {
    error.email = "Please enter valid email id";
    isValid = false;
  }

  if (!contactNo) {
    error.contactNo = "Please enter phone number";
    isValid = false;
  }
  if (!isPhoneValid(contactNo)) {
    error.contactNo = "Please enter a valid contact number";
    isValid = false;
  }

  if (roleIds.length === 0) {
    error.roleIds = "Please select role";
    isValid = false;
  }

  setErrors(error);
  return [isValid, error] as [boolean, { [key: string]: any }];
};
